// apps/vendor/src/features/announcements/AnnouncementBanner.tsx
//
// Vendor page-level announcement banner — parity with merchant-v3's
// components/topbar/AnnouncementBanner.tsx. Renders the first active
// announcement matching the requested `surface` (+ optional `category`).
// Click opens a detail modal with the full title + body; the X dismisses
// (localStorage, unless force_show).

import { useState, type JSX } from 'react';
import { Dialog, DialogTitle, DialogDescription } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import {
  AlertTriangleIcon,
  PercentIcon,
  SparklesIcon,
  XIcon,
} from './icons.js';
import {
  dismissAnnouncement,
  isAnnouncementDismissed,
  useAnnouncements,
  type Announcement,
  type AnnouncementCategory,
  type AnnouncementSurface,
} from './api.js';

type IconComponent = (props: { className?: string }) => JSX.Element;

const CATEGORY_ICON: Record<AnnouncementCategory, IconComponent> = {
  system_notice: AlertTriangleIcon,
  promo_marketing: PercentIcon,
  maintenance: AlertTriangleIcon,
  feature_release: SparklesIcon,
};

const CATEGORY_BANNER_CLASSES: Record<AnnouncementCategory, string> = {
  system_notice: 'border-orange-200 bg-orange-50 text-orange-900',
  promo_marketing: 'border-sky-200 bg-sky-50 text-sky-900',
  maintenance: 'border-red-200 bg-red-50 text-red-900',
  feature_release: 'border-emerald-200 bg-emerald-50 text-emerald-900',
};

const CATEGORY_AVATAR_CLASSES: Record<AnnouncementCategory, string> = {
  system_notice: 'bg-orange-100 text-orange-600',
  promo_marketing: 'bg-sky-100 text-sky-600',
  maintenance: 'bg-red-100 text-red-600',
  feature_release: 'bg-emerald-100 text-emerald-600',
};

function pickLocale(
  s: { en: string; vi?: string; fr?: string },
  lng: string,
): string {
  if (lng.startsWith('vi') && s.vi) return s.vi;
  if (lng.startsWith('fr') && s.fr) return s.fr;
  return s.en;
}

export interface AnnouncementBannerProps {
  surface: AnnouncementSurface;
  category?: AnnouncementCategory;
}

export function AnnouncementBanner({
  surface,
  category,
}: AnnouncementBannerProps): JSX.Element | null {
  const { t, i18n } = useTranslation('common');
  const params = category ? { surface, category } : { surface };
  const { data } = useAnnouncements(params);

  // Local dismiss tick so click-to-dismiss re-renders immediately (the hook
  // source is localStorage, which does not trigger React updates on its own).
  const [dismissTick, setDismissTick] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);

  // "dismissible wins" (user decision 2026-07-20): dismissible → honour close
  // state; non-dismissible → sticky. force_show is not a "can't close" flag.
  const visible = (data ?? []).filter(
    (a: Announcement) => (a.dismissible ? !isAnnouncementDismissed(a.id) : true),
  );
  void dismissTick;

  const first = visible[0];
  if (!first) return null;

  const Icon = CATEGORY_ICON[first.category];
  const title = pickLocale(first.title_i18n, i18n.language);
  const body = pickLocale(first.body_i18n, i18n.language);

  const handleDismiss = (): void => {
    dismissAnnouncement(first.id);
    setDismissTick((n) => n + 1);
  };

  return (
    <>
      <div
        role="status"
        data-testid="vendor-announcement-banner"
        data-announcement-id={first.id}
        className={`flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3 text-sm ${CATEGORY_BANNER_CLASSES[first.category]}`}
      >
        <button
          type="button"
          onClick={() => setDetailOpen(true)}
          className="flex flex-1 items-center gap-3 text-left"
          aria-label={t('announcement.viewDetails')}
        >
          <span
            className={`inline-flex h-9 w-9 flex-none items-center justify-center rounded-full ${CATEGORY_AVATAR_CLASSES[first.category]}`}
          >
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold">{title}</p>
            <p className="truncate text-xs opacity-80">{body}</p>
          </div>
        </button>
        {first.dismissible ? (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t('announcement.dismiss')}
            /* dark-ok: translucent wash over a colour-tinted banner, correct in both modes */
            className="inline-flex h-7 w-7 items-center justify-center rounded text-pw-text-muted hover:bg-black/5"
          >
            <XIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen} className="max-w-lg">
        <div data-testid="vendor-announcement-banner-detail">
          <div className="mb-4 flex items-start gap-3">
            <span
              className={`inline-flex h-10 w-10 flex-none items-center justify-center rounded-full ${CATEGORY_AVATAR_CLASSES[first.category]}`}
            >
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-base font-semibold text-pw-text">
                {title}
              </DialogTitle>
              <DialogDescription className="mt-1 whitespace-pre-wrap text-sm text-pw-text-muted">
                {body}
              </DialogDescription>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            {first.dismissible ? (
              <button
                type="button"
                onClick={() => {
                  handleDismiss();
                  setDetailOpen(false);
                }}
                className="rounded-md border border-pw-border px-3 py-1.5 text-sm font-medium text-pw-text-muted hover:bg-pw-surface"
              >
                {t('announcement.dismiss')}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setDetailOpen(false)}
              className="rounded-md bg-pw-text px-3 py-1.5 text-sm font-medium text-pw-bg hover:opacity-90"
            >
              {t('announcement.close')}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default AnnouncementBanner;
